import { 
  Search,
  FileText,
  Wrench,
  Zap,
  ArrowRight,
  CheckCircle2,
  Clock,
  Users,
  ClipboardCheck,
  Settings
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

export const Process = ({ onContactClick }) => {
  const processSteps = [
    {
      number: '01',
      title: 'Erstberatung & Lastanalyse',
      duration: '1-2 Wochen',
      icon: Search,
      description: 'Wir analysieren Ihr Lastprofil und ermitteln das optimale Speicherkonzept für Ihre Anforderungen.',
      deliverables: [
        'Lastprofilanalyse (15-Minuten-Werte)',
        'Identifikation von Einsparpotenzialen',
        'Erste Wirtschaftlichkeitsabschätzung',
        'Konzeptempfehlung',
      ],
      yourTasks: [
        'Lastgangdaten bereitstellen (12 Monate)',
        'Informationen zu bestehender PV-Anlage',
        'Stromrechnungen der letzten 2 Jahre',
      ],
    },
    {
      number: '02',
      title: 'Detailplanung',
      duration: '3-5 Wochen',
      icon: FileText,
      description: 'Technische Planung, Wirtschaftlichkeitsberechnung und Angebotserstellung.',
      deliverables: [
        'Detaillierte technische Planung',
        'Vollständige Wirtschaftlichkeitsrechnung',
        'Verbindliches Angebot',
        'Finanzierungsoptionen',
      ],
      yourTasks: [
        'Gebäudepläne und technische Dokumentation',
        'Abstimmung mit Facility Management',
        'Prüfung der Finanzierungsoptionen',
      ],
    },
    {
      number: '03',
      title: 'Genehmigung & Beschaffung',
      duration: '4-8 Wochen',
      icon: ClipboardCheck,
      description: 'Einholung aller notwendigen Genehmigungen und Beschaffung der Komponenten.',
      deliverables: [
        'Baugenehmigung (falls erforderlich)',
        'Netzanmeldung beim Netzbetreiber',
        'Bestellung der Komponenten',
        'Projektterminplan',
      ],
      yourTasks: [
        'Interne Freigabe / Vorstandsbeschluss',
        'Bereitstellung Ansprechpartner',
        'Zugang für Vor-Ort-Termine',
      ],
    },
    {
      number: '04',
      title: 'Installation',
      duration: '4-8 Wochen',
      icon: Wrench,
      description: 'Schlüsselfertige Errichtung durch zertifizierte Fachpartner.',
      deliverables: [
        'Aufstellung Containersystem oder Innenaufstellung',
        'Elektrische Installation',
        'Anbindung an Gebäudeleittechnik',
        'Funktionsprüfung',
      ],
      yourTasks: [
        'Zugang zum Aufstellort',
        'Koordination mit Haustechnik',
        'Freischaltungen nach Abstimmung',
      ],
    },
    {
      number: '05',
      title: 'Inbetriebnahme & Optimierung',
      duration: '2-4 Wochen',
      icon: Settings,
      description: 'Inbetriebnahme, Einstellung der Betriebsparameter und erste Optimierung.',
      deliverables: [
        'Inbetriebnahmeprotokoll',
        'Einweisung des Betriebspersonals',
        'Feinabstimmung der Parameter',
        'Dokumentation',
      ],
      yourTasks: [
        'Personal für Einweisung benennen',
        'Feedback zu Betriebserfahrungen',
      ],
    },
    {
      number: '06',
      title: 'Betrieb & Monitoring',
      duration: 'Dauerhaft',
      icon: Zap,
      description: 'Laufender Betrieb mit 24/7 Monitoring und kontinuierlicher Optimierung.',
      deliverables: [
        '24/7 Fernüberwachung',
        'Monatliche Performance-Reports',
        'Jährliche Wartung',
        'Störungsmanagement',
      ],
      yourTasks: [
        'Meldung besonderer Ereignisse',
        'Jährliche Wartungstermine koordinieren',
      ],
    },
  ];

  return (
    <main className="pt-16 lg:pt-20">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-slate-50 to-white" data-testid="process-hero">
        <div className="container-main">
          <div className="max-w-3xl">
            <span className="text-[#10B981] font-medium text-sm uppercase tracking-wide">Prozess</span>
            <h1 className="heading-xl text-[#0F172A] mt-2 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Strukturiert von der Analyse zur Installation
            </h1>
            <p className="text-lg text-slate-600 mb-8">
              Ein bewährter Projektablauf mit klaren Meilensteinen, transparenter Kommunikation 
              und minimaler Belastung für Ihren Krankenhausbetrieb.
            </p>
            <div className="flex flex-wrap gap-6">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#10B981]" />
                <span className="text-slate-600">Gesamtdauer: 4-6 Monate</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-[#10B981]" />
                <span className="text-slate-600">Fester Projektleiter</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline Overview */}
      <section className="section-padding bg-white" data-testid="timeline-overview">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="heading-lg text-[#0F172A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Projektphasen im Überblick
            </h2>
          </div>

          {/* Visual Timeline */}
          <div className="relative mb-16">
            <div className="hidden lg:block absolute top-8 left-0 right-0 h-1 bg-slate-200"></div>
            <div className="grid lg:grid-cols-6 gap-4">
              {processSteps.map((step, index) => (
                <div key={index} className="relative text-center">
                  <div className="lg:mb-8">
                    <div className="w-16 h-16 bg-[#0F172A] rounded-full flex items-center justify-center mx-auto relative z-10">
                      <step.icon className="w-7 h-7 text-white" />
                    </div>
                  </div>
                  <div className="text-sm font-medium text-[#10B981] mb-1">{step.duration}</div>
                  <div className="font-semibold text-[#0F172A] text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {step.title}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Process Steps */}
      <section className="section-padding bg-slate-50" data-testid="detailed-steps">
        <div className="container-main">
          <div className="space-y-6">
            {processSteps.map((step, index) => (
              <Card key={index} className="bg-white overflow-hidden" data-testid={`step-${index + 1}`}>
                <CardContent className="p-0">
                  <div className="grid lg:grid-cols-12 gap-0">
                    {/* Step Header */}
                    <div className="lg:col-span-3 bg-slate-50 p-6 lg:p-8 border-b lg:border-b-0 lg:border-r border-slate-100">
                      <div className="flex items-center gap-4 mb-4">
                        <span className="text-4xl font-bold text-slate-200" style={{ fontFamily: 'Outfit, sans-serif' }}>
                          {step.number}
                        </span>
                        <div className="w-12 h-12 bg-emerald-50 rounded-lg flex items-center justify-center">
                          <step.icon className="w-6 h-6 text-[#10B981]" />
                        </div>
                      </div>
                      <h3 className="text-xl font-semibold text-[#0F172A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {step.title}
                      </h3>
                      <div className="inline-flex items-center gap-2 bg-emerald-50 rounded-full px-3 py-1">
                        <Clock className="w-4 h-4 text-[#10B981]" />
                        <span className="text-sm text-[#10B981] font-medium">{step.duration}</span>
                      </div>
                    </div>

                    {/* Step Content */}
                    <div className="lg:col-span-9 p-6 lg:p-8">
                      <p className="text-slate-600 mb-6">{step.description}</p>

                      <div className="grid md:grid-cols-2 gap-6">
                        {/* Deliverables */}
                        <div>
                          <h4 className="font-semibold text-[#0F172A] mb-3 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            <CheckCircle2 className="w-5 h-5 text-[#10B981]" />
                            Unsere Leistungen
                          </h4>
                          <ul className="space-y-2">
                            {step.deliverables.map((item, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                                <span className="w-1.5 h-1.5 bg-[#10B981] rounded-full mt-2 flex-shrink-0"></span>
                                {item}
                              </li>
                            ))}
                          </ul>
                        </div>

                        {/* Your Tasks */}
                        <div>
                          <h4 className="font-semibold text-[#0F172A] mb-3 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            <Users className="w-5 h-5 text-blue-500" />
                            Was wir von Ihnen benötigen
                          </h4>
                          <ul className="space-y-2">
                            {step.yourTasks.map((item, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                                {item}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* What We Need From You */}
      <section className="section-padding bg-white" data-testid="requirements">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="heading-lg text-[#0F172A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Was wir für eine erste Einschätzung brauchen
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Mit diesen Informationen können wir Ihnen schnell eine belastbare Potenzialanalyse liefern.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <Card className="card-feature">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-emerald-50 rounded-lg flex items-center justify-center mb-4">
                  <span className="text-xl font-bold text-[#10B981]">1</span>
                </div>
                <h3 className="text-lg font-semibold text-[#0F172A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Lastgangdaten
                </h3>
                <p className="text-sm text-slate-600 mb-4">
                  15-Minuten-Messwerte Ihres Stromverbrauchs, idealerweise für 12 Monate. 
                  Diese erhalten Sie von Ihrem Netzbetreiber.
                </p>
                <span className="text-xs text-slate-400">Format: Excel/CSV</span>
              </CardContent>
            </Card>

            <Card className="card-feature">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-emerald-50 rounded-lg flex items-center justify-center mb-4">
                  <span className="text-xl font-bold text-[#10B981]">2</span>
                </div>
                <h3 className="text-lg font-semibold text-[#0F172A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Stromrechnungen
                </h3>
                <p className="text-sm text-slate-600 mb-4">
                  Jahresrechnungen der letzten 1-2 Jahre mit Aufschlüsselung nach 
                  Arbeitspreis, Leistungspreis und Netzentgelten.
                </p>
                <span className="text-xs text-slate-400">Format: PDF</span>
              </CardContent>
            </Card>

            <Card className="card-feature">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-emerald-50 rounded-lg flex items-center justify-center mb-4">
                  <span className="text-xl font-bold text-[#10B981]">3</span>
                </div>
                <h3 className="text-lg font-semibold text-[#0F172A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  PV-Informationen
                </h3>
                <p className="text-sm text-slate-600 mb-4">
                  Falls vorhanden: Leistung der PV-Anlage, Erzeugungsdaten und 
                  aktuelle Eigenverbrauchsquote.
                </p>
                <span className="text-xs text-slate-400">Falls vorhanden</span>
              </CardContent>
            </Card>
          </div>

          <div className="mt-8 p-6 bg-blue-50 rounded-xl border border-blue-100 text-center">
            <p className="text-blue-800">
              <strong>Keine Lastdaten zur Hand?</strong> Kein Problem – wir helfen Ihnen, diese beim 
              Netzbetreiber anzufordern. Der Prozess dauert meist nur wenige Tage.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-[#0F172A] text-white" data-testid="process-cta">
        <div className="container-main text-center">
          <h2 className="heading-lg mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Bereit für den ersten Schritt?
          </h2>
          <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
            Kontaktieren Sie uns für eine unverbindliche Erstberatung. 
            Wir analysieren Ihr Potenzial – kostenlos und ohne Verpflichtungen.
          </p>
          <Button 
            onClick={onContactClick}
            className="bg-[#10B981] hover:bg-emerald-600 text-white px-8 py-4 rounded-md font-medium inline-flex items-center gap-2"
            data-testid="process-final-cta"
          >
            Erstberatung anfragen
            <ArrowRight className="w-5 h-5" />
          </Button>
        </div>
      </section>
    </main>
  );
};
